import { useState, useCallback } from 'react';
import { Check, Scale, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import { AnalyticalBalance, VolumetricFlask } from '@/components/lab';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Laboratory Safety for Mass Measurements',
    content: `Accurate mass measurements require careful handling of chemicals and equipment. Always wear safety goggles to protect against accidental spills. Keep the balance area clean and dry. Some compounds (like CuSO4) can cause skin irritation - wash hands thoroughly after handling. Never place chemicals directly on the balance pan; use weighing paper or a tared container.`
  },
  2: {
    title: 'Understanding Molar Mass',
    content: `Molar mass is the mass of one mole of a substance, expressed in g/mol. It numerically equals the molecular weight (sum of atomic masses). For example, NaCl has molar mass = 22.99 (Na) + 35.45 (Cl) = 58.44 g/mol. This means 58.44 grams of NaCl contains exactly 6.022 x 10^23 formula units (Avogadro's number). Molar mass is the bridge between macroscopic mass and microscopic particle count.`
  },
  3: {
    title: 'Precise Mass Measurement',
    content: `Analytical balances measure to 0.0001 g (0.1 mg) precision. For accurate readings: (1) ensure the balance is level and calibrated, (2) close draft shields during measurement, (3) wait for the reading to stabilize, (4) record all digits displayed. Mass is a fundamental measurement in chemistry - combined with molar mass, it allows calculation of moles: n = m/M, where n is moles, m is mass, and M is molar mass.`
  }
};

export default function Part1Introduction() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [compoundSelected, setCompoundSelected] = useState<string | null>(null);
  const [massRecorded, setMassRecorded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [sampleMass, setSampleMass] = useState(0);

  const compounds = [
    { id: 'NaCl', name: 'Sodium Chloride', formula: 'NaCl', molarMass: 58.44 },
    { id: 'CuSO4', name: 'Copper Sulfate', formula: 'CuSO₄·5H₂O', molarMass: 249.69 },
    { id: 'Na2CO3', name: 'Sodium Carbonate', formula: 'Na₂CO₃', molarMass: 105.99 }
  ];

  const completedSteps = [safetyAcknowledged, compoundSelected, massRecorded].filter(Boolean).length;

  const handleSafety = useCallback(() => {
    if (!safetyAcknowledged) {
      setSafetyAcknowledged(true);
      setCurrentStep(2);
    }
  }, [safetyAcknowledged]);

  const handleSelectCompound = useCallback((id: string) => {
    if (safetyAcknowledged && !compoundSelected) {
      setCompoundSelected(id);
      setCurrentStep(3);
    }
  }, [safetyAcknowledged, compoundSelected]);

  const handleWeigh = useCallback(() => {
    if (compoundSelected && !massRecorded) {
      const mass = 2.5 + Math.random() * 0.5;
      setSampleMass(parseFloat(mass.toFixed(4)));
      setMassRecorded(true);
    }
  }, [compoundSelected, massRecorded]);

  const selectedCompound = compounds.find(c => c.id === compoundSelected);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Scale size={16} />
          <h3>Part I: Introduction</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Safety Review', hint: 'Acknowledge safety procedures', done: safetyAcknowledged },
            { id: 2, title: 'Select Compound', hint: 'Choose compound to analyze', done: !!compoundSelected },
            { id: 3, title: 'Record Mass', hint: 'Weigh sample on balance', done: massRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {massRecorded && selectedCompound && (
          <div className="results-panel">
            <h4>Sample Information</h4>
            <div className="result-row">
              <span>Compound:</span>
              <span className="value">{selectedCompound.formula}</span>
            </div>
            <div className="result-row">
              <span>Mass:</span>
              <span className="value">{sampleMass} g</span>
            </div>
            <div className="result-row">
              <span>Molar Mass:</span>
              <span className="value">{selectedCompound.molarMass} g/mol</span>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Molar Mass Introduction"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Analytical balance', 'Volumetric flask', 'Compounds']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="molar-workspace">
          {/* Safety Acknowledgment */}
          {!safetyAcknowledged && (
            <div className="safety-panel" onClick={handleSafety} style={{ cursor: 'pointer' }}>
              <h3>Safety Review</h3>
              <ul>
                <li>Wear safety goggles at all times</li>
                <li>Handle chemicals with care</li>
                <li>Clean up spills immediately</li>
              </ul>
              <button className="acknowledge-btn">
                <Check size={16} />
                I Understand
              </button>
            </div>
          )}

          {/* Compound Selection */}
          {safetyAcknowledged && !compoundSelected && (
            <div className="compound-selection">
              <h3>Select a Compound</h3>
              <div className="compound-grid">
                {compounds.map((compound) => (
                  <div
                    key={compound.id}
                    className="compound-card"
                    onClick={() => handleSelectCompound(compound.id)}
                  >
                    <span className="compound-formula">{compound.formula}</span>
                    <span className="compound-name">{compound.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Weighing */}
          {compoundSelected && (
            <div className="equipment-row">
              <div
                className={`equipment-station ${!massRecorded ? 'clickable' : ''}`}
                onClick={handleWeigh}
              >
                <AnalyticalBalance
                  reading={massRecorded ? sampleMass : 0}
                  stable={massRecorded}
                  unit="g"
                />
                <span className="equipment-label">Analytical Balance</span>
              </div>

              <div className="equipment-station">
                <VolumetricFlask
                  size={100}
                  fillLevel={0}
                  label="100 mL"
                />
                <span className="equipment-label">Volumetric Flask</span>
              </div>
            </div>
          )}

          {massRecorded && (
            <div className="status-message success">
              <Check size={20} />
              Mass recorded: {sampleMass} g of {selectedCompound?.formula}
            </div>
          )}
        </div>

        <style>{`
          .molar-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .safety-panel {
            background: rgba(251, 191, 36, 0.1);
            border: 1px solid rgba(251, 191, 36, 0.3);
            border-radius: 12px;
            padding: 24px;
            max-width: 400px;
            text-align: center;
          }

          .safety-panel h3 {
            color: #FBBF24;
            margin: 0 0 16px 0;
          }

          .safety-panel ul {
            text-align: left;
            margin: 0 0 16px 0;
            padding-left: 20px;
          }

          .safety-panel li {
            margin: 8px 0;
            color: var(--text-secondary);
          }

          .acknowledge-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 auto;
            padding: 10px 20px;
            background: var(--secondary);
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 500;
          }

          .compound-selection h3 {
            margin: 0 0 16px 0;
            color: var(--text-primary);
          }

          .compound-grid {
            display: flex;
            gap: 16px;
          }

          .compound-card {
            background: var(--bg-elevated);
            border: 1px solid var(--border);
            border-radius: 8px;
            padding: 16px 24px;
            cursor: pointer;
            transition: all 0.2s;
            text-align: center;
          }

          .compound-card:hover {
            border-color: var(--secondary);
            background: rgba(0, 168, 107, 0.1);
          }

          .compound-formula {
            display: block;
            font-size: 18px;
            font-weight: 600;
            color: var(--secondary);
            margin-bottom: 4px;
          }

          .compound-name {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .equipment-row {
            display: flex;
            gap: 60px;
            align-items: flex-end;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .results-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-top: 16px;
          }

          .results-panel h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .result-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13px;
          }

          .result-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp4"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </LabBench>
    </div>
  );
}
